import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import { useMemo } from "react";
import _ from "lodash";
import { useRealmApp } from "../RealmApp";
import { useTimezone } from "../helpers/hooks";
import useTaxes from "./useTaxes";
import { consolidateSummaryWithType } from "../helpers/dataHelper";
import { useDatesContext } from "context/DatesContext";

const useOrders = (storeId, range) => {
  const app = useRealmApp();
  const { orders, loading: loading1 } = useAllOrders(
    app.currentUser.id,
    storeId,
    range.start,
    range.end
  );

  const { taxes, loading: loading2 } = useTaxes();
  const taxesMap = useMemo(() => {
    const result = {};
    taxes.forEach((tax) => (result[tax._id] = tax));
    return result;
  }, [taxes]);

  const ordersWithData = useMemo(() => {
    if (loading1 || loading2) {
      return [];
    }
    const result = orders.map((order) => _.cloneDeep(order));
    result.forEach((order) => {
      if (order.tax1Id) {
        order.tax1Name =
          taxesMap[order.tax1Id]?.taxInvoiceName ||
          taxesMap[order.tax1Id]?.name ||
          `${order.tax1Id}`;
        order.tax1ImpName = taxesMap[order.tax1Id]
          ? taxesMap[order.tax1Id].taxBaseName ||
            `Base imponible ${taxesMap[order.tax1Id].name}`
          : `Imp. ${order.tax1Id}`;
      }
      if (order.tax2Id) {
        order.tax2Name =
          taxesMap[order.tax2Id]?.taxInvoiceName ||
          taxesMap[order.tax2Id]?.name ||
          `${order.tax2Id}`;
        order.tax2ImpName = taxesMap[order.tax2Id]
          ? taxesMap[order.tax2Id].taxBaseName ||
            `Base imponible ${taxesMap[order.tax2Id].name}`
          : `Imp. ${order.tax2Id}`;
      }
      if (order.tax3Id) {
        order.tax3Name =
          taxesMap[order.tax3Id]?.taxInvoiceName ||
          taxesMap[order.tax3Id]?.name ||
          `${order.tax3Id}`;
        order.tax3ImpName = taxesMap[order.tax3Id]
          ? taxesMap[order.tax3Id].taxBaseName ||
            `Base imponible ${taxesMap[order.tax3Id].name}`
          : `Imp. ${order.tax3Id}`;
      }
      if (order.tax4Id) {
        order.tax4Name =
          taxesMap[order.tax4Id]?.taxInvoiceName ||
          taxesMap[order.tax4Id]?.name ||
          `${order.tax4Id}`;
        order.tax4ImpName = taxesMap[order.tax4Id]
          ? taxesMap[order.tax4Id].taxBaseName ||
            `Base imponible ${taxesMap[order.tax4Id].name}`
          : `Imp. ${order.tax4Id}`;
      }
    });
    return result;
  }, [orders, taxesMap, loading1, loading2]);

  return {
    loading: loading1 || loading2,
    orders: ordersWithData,
  };
};

export const useOrderDates = (storeId, start, end, type) => {
  const { orderDateSummary, loading } = useAllOrderDateSummary(
    storeId,
    start,
    end,
    type
  );

  const orderDateSummaryConsolidated = useMemo(
    () =>
      consolidateSummaryWithType(
        orderDateSummary,
        (data) => `${data._id.year}_${data._id.month}_${data._id.dayOfMonth}`
      ),
    [orderDateSummary]
  );

  return {
    loading,
    orderDateSummary: orderDateSummaryConsolidated,
    orderDateSummaryRaw: orderDateSummary,
  };
};

export const useOrderHourly = (storeId, start, end, type) => {
  const { orderHourlySummary, loading } = useAllOrderHourlySummary(
    storeId,
    start,
    end,
    type
  );

  const orderHourlySummaryConsolidated = useMemo(
    () =>
      consolidateSummaryWithType(
        orderHourlySummary,
        (data) =>
          `${data._id.year}_${data._id.month}_${data._id.dayOfMonth}_${data._id.hour}_${data.extraAmount1}`
      ),
    [orderHourlySummary]
  );

  return {
    loading,
    orderHourlySummary: orderHourlySummaryConsolidated,
    orderHourlySummaryRaw: orderHourlySummary,
  };
};

export const useOrderMonthly = (storeId, start, end, type) => {
  const { orderMonthlySummary, loading } = useAllOrderMonthlySummary(
    storeId,
    start,
    end,
    type
  );

  const orderMonthlySummaryConsolidated = useMemo(
    () =>
      consolidateSummaryWithType(
        orderMonthlySummary,
        (data) => `${data._id.year}_${data._id.month}`
      ),
    [orderMonthlySummary]
  );

  return {
    loading,
    orderMonthlySummary: orderMonthlySummaryConsolidated,
    orderMonthlySummaryRaw: orderMonthlySummary,
  };
};

export default useOrders;

function useAllOrders(ownerId, storeId, start, end) {
  const orderDateInput = {
    storeId,
    start,
    end,
  };
  const { getCacheControl } = useDatesContext();
  const fetchPolicy = getCacheControl("useAllOrders", 60 * 1000);
  const { data, loading, error } = useQuery(
    gql`
      query GetOrderDate($orderDateInput: OrderDateInput!) {
        orderDate(input: $orderDateInput) {
          _id
          owner_id
          balance
          billingUserId
          billingUserName
          businessId
          businessName
          change
          creationDatetime
          customerAddress
          customerAddress2
          customerId
          customerIdNumber
          customerName
          customerZip
          discountItemId
          invoiceNumber
          invoiceSerial
          invoiceSequence
          items
          mode
          net
          openUserId
          openUserName
          payment
          payments
          printCount
          qrData
          extraAmount1
          qrText
          quantity
          refundStatus
          refundingOrderId
          refundingOrderNumber
          status
          storeId
          subtype
          tax1
          tax1Id
          tax1Imp
          tax2
          tax2Id
          tax2Imp
          tax3
          tax3Id
          tax3Imp
          tax4
          tax4Id
          tax4Imp
          terminalId
          total
          type
          updateDatetime
          zReportId
        }
      }
    `,
    {
      variables: {
        orderDateInput,
      },
      fetchPolicy,
    }
  );
  if (error) {
    throw new Error(`Failed to fetch tasks: ${error.message}`);
  }
  // If the query has finished, return the tasks from the result data
  // Otherwise, return an empty list
  let orders = useMemo(() => data?.orderDate ?? [], [data]);

  if (!Array.isArray(orders)) {
    orders = [orders];
  }

  const sortedOrders = useMemo(() => {
    return orders
      .slice()
      .sort((a, b) => new Date(b.updateDatetime) - new Date(a.updateDatetime));
  }, [orders]);

  return { orders: sortedOrders, loading };
}

function useAllOrderDateSummary(storeId, start, end, type) {
  const timezone = useTimezone();
  const { getCacheControl } = useDatesContext();
  const fetchPolicy = getCacheControl("useAllOrderPaymentSummary", 60 * 1000);
  const { data, loading, error } = useQuery(
    gql`
      query GetOrderDateSummary(
        $orderDateSummaryInput: OrderDateSummaryInput!
      ) {
        orderDateSummary(input: $orderDateSummaryInput) {
          _id {
            year
            month
            dayOfMonth
          }
          extraAmount1
          total
          tax1
          quantity
          count
        }
      }
    `,
    {
      variables: {
        orderDateSummaryInput: {
          timezone,
          storeId,
          start,
          end,
          type,
        },
      },
      fetchPolicy,
    }
  );
  if (error) {
    throw new Error(`Failed to fetch tasks: ${error.message}`);
  }
  // If the query has finished, return the tasks from the result data
  // Otherwise, return an empty list
  const orderDateSummary = data?.orderDateSummary ?? [];

  return { orderDateSummary, loading };
}

function useAllOrderHourlySummary(storeId, start, end, type) {
  const timezone = useTimezone();
  const { getCacheControl } = useDatesContext();
  const fetchPolicy = getCacheControl("useAllOrderHourlySummary", 60 * 1000);
  // console.log("fetchPolicy", fetchPolicy);
  const { data, loading, error } = useQuery(
    gql`
      query GetOrderHourlySummary(
        $orderHourlySummaryInput: OrderHourlySummaryInput!
      ) {
        orderHourlySummary(input: $orderHourlySummaryInput) {
          _id {
            year
            month
            dayOfMonth
            hour
            type
          }
          extraAmount1
          total
          tax1
          quantity
          count
        }
      }
    `,
    {
      variables: {
        orderHourlySummaryInput: {
          storeId,
          start,
          end,
          timezone,
          type,
        },
      },
      fetchPolicy,
    }
  );
  if (error) {
    throw new Error(`Failed to fetch tasks: ${error.message}`);
  }
  // If the query has finished, return the tasks from the result data
  // Otherwise, return an empty list
  //

  let orderHourlySummary = useMemo(
    () => data?.orderHourlySummary ?? [],
    [data]
  );

  return { orderHourlySummary, loading };
}

function useAllOrderMonthlySummary(storeId, start, end, type) {
  const timezone = useTimezone();
  const { data, loading, error } = useQuery(
    gql`
      query GetOrderMonthlySummary(
        $orderMonthlySummaryInput: OrderMonthlySummaryInput!
      ) {
        orderMonthlySummary(input: $orderMonthlySummaryInput) {
          _id {
            year
            month
            type
          }
          total
          tax1
          quantity
          count
        }
      }
    `,
    {
      variables: {
        orderMonthlySummaryInput: {
          timezone,
          storeId,
          start,
          end,
          type,
        },
      },
    }
  );
  if (error) {
    throw new Error(`Failed to fetch tasks: ${error.message}`);
  }
  // If the query has finished, return the tasks from the result data
  // Otherwise, return an empty list
  //

  let orderMonthlySummary = useMemo(
    () => data?.orderMonthlySummary ?? [],
    [data]
  );

  return { orderMonthlySummary, loading };
}
